import { getCustomerDisplayName } from '../../utils/customer';

/**
 * DeliveryReportButton Component
 *
 * Button card in the work order sidebar that generates and opens a delivery
 * report, combining work order, inspection, checklist and work path data.
 *
 * **Loads nothing until asked (#2414).** This card was removed a day after it
 * shipped (91e1b24c, 31.3.2026) because the work-order page froze: too many
 * components firing inspection queries at once. The underlying hook has since
 * become a plain `useQuery` with a stable key, so React Query dedupes — but
 * deduping only removes the duplicate request, not the request itself. Fetching
 * on mount would still put three extra queries on every work-order open for a
 * report most visits never print.
 *
 * So the queries stay disabled (`undefined` booking id) until the first click.
 * The click arms them and the effect below prints once the data lands, which
 * also means the button cannot print a half-loaded report.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { generateDeliveryReport } from './DeliveryReportView';
import { useBookingInspections } from '@/hooks/useInspections';
import { useBookingChecklists } from '@/hooks/useChecklists';
import { useBookingWorkPaths } from '@/hooks/useWorkPaths';
import { useBranding } from '@/hooks/useBranding';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import type { Inspection } from '@/types';

interface Props {
  workOrder: WorkOrderDetail;
  cardVariant?: 'outlined' | 'elevation';
}

export function DeliveryReportButton({ workOrder, cardVariant = 'outlined' }: Props) {
  const { t } = useTranslation('workorders');
  const branding = useBranding();

  // Undefined until the user asks for the report, which leaves every query
  // disabled — see the note above on why deduping alone is not enough.
  const [armed, setArmed] = useState(false);
  // The three hooks disagree on the "no id" type — inspections takes
  // `| undefined`, the other two `| null` — but all three treat it the same:
  // the query stays disabled.
  const [idOrUndefined, idOrNull] = armed
    ? ([workOrder.id, workOrder.id] as const)
    : ([undefined, null] as const);

  const inspectionsQuery = useBookingInspections(idOrUndefined);
  const checklistsQuery = useBookingChecklists(idOrNull);
  const workPathsQuery = useBookingWorkPaths(idOrNull);

  const isLoading =
    armed &&
    (inspectionsQuery.isLoading || checklistsQuery.isLoading || workPathsQuery.isLoading);

  const print = useCallback(() => {
    const customerName = getCustomerDisplayName(workOrder.customer);

    const mechanicName = workOrder.mechanic
      ? [workOrder.mechanic.firstName, workOrder.mechanic.lastName].filter(Boolean).join(' ')
      : undefined;

    const inspections = inspectionsQuery.data ?? [];
    // Map API inspection to types/Inspection format if available
    const inspection: Inspection | undefined = inspections[0]
      ? (inspections[0] as unknown as Inspection)
      : undefined;

    generateDeliveryReport({
      workOrder: {
        id: workOrder.id,
        workOrderNumber: workOrder.workOrderNumber ?? `WO-${workOrder.id.slice(0, 8).toUpperCase()}`,
        vehicleRegistration: workOrder.vehicle.registrationNumber,
        vehicleVin: undefined,
        vehicleMake: workOrder.vehicle.make ?? undefined,
        vehicleModel: workOrder.vehicle.model ?? undefined,
        vehicleYear: workOrder.vehicle.year ?? undefined,
        mileage: workOrder.vehicle.mileage ?? undefined,
        customerName,
        services: workOrder.services.map((s) => ({ name: s.name, status: s.status })),
        completedAt: workOrder.completedAt ?? undefined,
      },
      inspection,
      checklists: checklistsQuery.data ?? undefined,
      workPaths: workPathsQuery.data ?? undefined,
      workshopName: branding?.companyName ?? undefined,
      workshopAddress: undefined,
      workshopPhone: undefined,
      workshopEmail: undefined,
      mechanicName,
    });
  }, [workOrder, inspectionsQuery.data, checklistsQuery.data, workPathsQuery.data, branding]);

  // Print once the armed queries have settled. Disarming immediately keeps the
  // effect from firing again on a later refetch.
  useEffect(() => {
    if (!armed || isLoading) return;
    setArmed(false);
    print();
    // `print` is deliberately out of the dependency list: it changes identity
    // whenever the query data does, which would re-enter this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, isLoading]);

  return (
    <Card variant={cardVariant} sx={{ mb: 2 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={isLoading ? <CircularProgress size={16} /> : <PrintIcon />}
          onClick={() => { setArmed(true); }}
          disabled={isLoading}
          fullWidth
        >
          {t('deliveryReport.print')}
        </Button>
      </CardContent>
    </Card>
  );
}
